import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import Card from "../layout/Card";
import {
  ExclamationCircleIcon,
  ClockIcon,
  PhotoIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import DisabledTextInput from "../forms/DisabledTextInput";
import DangerZone from "../forms/DangerZone";
import { useSearchParams } from "react-router-dom";
import {
  CampaignState,
  createPinnedCampaign,
  createRotatingCampaign,
  createTemplateCampaign,
  deleteCampaigns,
  getCampaigns,
} from "../../slices/content";
import HorizontalRadio from "../forms/HorizontalRadio";
import Dropdown from "../forms/Dropdown";
import UploadFileDropzone from "../forms/UploadImageDropzone";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import DatetimeSelector from "../forms/DatetimeSelector";
import { campaignTemplates } from "../../utils/content";
import TimeSelector from "../forms/TimeSelector";
import { daysOfMonth, daysOfWeek } from "../../utils/calendar";
import {
  buildDateObjectFromHourAndMinute,
  buildDatetimeObjectFromStringAndNumbers,
  formatDateAsString,
} from "../../utils/datetime";
import Filters from "../layout/Filters";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Campaigns() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchType = searchParams.get("type");
  const dateRangeTerm = searchParams.get("dateRange");

  // Fetch data from server
  useEffect(() => {
    dispatch(getCampaigns({ type: searchType, dateRange: dateRangeTerm }));
    dispatch(getRooms());
  }, [dateRangeTerm, dispatch, searchType]);

  // Set state
  const [selectedCampaign, setSelectedCampaign] =
    useState<CampaignState | null>(null);
  const [createCampaignForm, setCreateCampaignForm] = useState<{
    data_type: string;
    build_type: string;
    name: { [key: string]: string };
    description: { [key: string]: string };
    display_type: string;
    document: string;
    background_image_selection: string;
    background_image: string;
    background_image_array: string[];
    header: { [key: string]: string };
    primary_text: { [key: string]: string };
    secondary_text: { [key: string]: string };
    title: { [key: string]: string };
    body: { [key: string]: string };
    choose_rooms_by: string;
    rooms: string[];
    tags: string[];
    template_type: string;
  }>({
    data_type: "static",
    build_type: "custom",
    name: { EN: "", FR: "", IT: "", DE: "" },
    description: { EN: "", FR: "", IT: "", DE: "" },
    display_type: "R",
    document: "D",
    background_image_selection: "catalog",
    background_image:
      "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/purple.png",
    background_image_array: [],
    header: { EN: "", FR: "", IT: "", DE: "" },
    primary_text: { EN: "", FR: "", IT: "", DE: "" },
    secondary_text: { EN: "", FR: "", IT: "", DE: "" },
    title: { EN: "", FR: "", IT: "", DE: "" },
    body: { EN: "", FR: "", IT: "", DE: "" },
    choose_rooms_by: "name",
    rooms: [],
    tags: [],
    template_type: "",
  });
  const [createCustomCampaignStartDate, setCreateCustomCampaignStartDate] =
    useState<any>({
      startDate: null,
      endDate: null,
    });
  const [createCustomCampaignStartTime, setCreateCustomCampaignStartTime] =
    useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [createCustomCampaignEndDate, setCreateCustomCampaignEndDate] =
    useState<any>({
      startDate: null,
      endDate: null,
    });
  const [createCustomCampaignEndTime, setCreateCustomCampaignEndTime] =
    useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [
    createDynamicCampaignScheduleType,
    setCreateDynamicCampaignScheduleType,
  ] = useState<{ value: string }>({ value: "STD" });
  const [
    createDynamicCampaignScheduleStdStart,
    setCreateDynamicCampaignScheduleStdStart,
  ] = useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [
    createDynamicCampaignScheduleStdEnd,
    setCreateDynamicCampaignScheduleStdEnd,
  ] = useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [
    createDynamicCampaignScheduleStw,
    setCreateDynamicCampaignScheduleStw,
  ] = useState<any[]>([]);
  const [
    createDynamicCampaignScheduleStwStart,
    setCreateDynamicCampaignScheduleStwStart,
  ] = useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [
    createDynamicCampaignScheduleStwEnd,
    setCreateDynamicCampaignScheduleStwEnd,
  ] = useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [
    createDynamicCampaignScheduleStm,
    setCreateDynamicCampaignScheduleStm,
  ] = useState<any[]>([]);
  const [
    createDynamicCampaignScheduleStmStart,
    setCreateDynamicCampaignScheduleStmStart,
  ] = useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [
    createDynamicCampaignScheduleStmEnd,
    setCreateDynamicCampaignScheduleStmEnd,
  ] = useState<{ hour: any; minute: any }>({ hour: "", minute: "" });
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [type, setType] = useState({ value: "" });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { campaigns, options } = useAppSelector((state) => state.content);
  const {
    images: { campaigns: campaignImages },
  } = useAppSelector((state) => state.uploads);
  const { rooms } = useAppSelector((state) => state.rooms);
  const { images } = useAppSelector((state) => state.uploads);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectCampaignToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [campaignId] = args;
    const campaignData = campaignsData.filter(
      (campaign) => campaign.id === campaignId,
    )[0];
    setSelectedCampaign(campaignData);
    setShowEditModal(true);
  }

  function openCreateCampaignModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreateCampaignForm({
        ...createCampaignForm,
        rooms: allAvailableRooms,
      });
    } else {
      setCreateCampaignForm({ ...createCampaignForm, rooms: [] });
    }
  }

  function updateRoomForCampaign(roomId: string) {
    if (createCampaignForm.rooms.includes(roomId)) {
      const updatedRooms = createCampaignForm.rooms.filter((x) => x !== roomId);
      setCreateCampaignForm({ ...createCampaignForm, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createCampaignForm.rooms, roomId];
      setCreateCampaignForm({ ...createCampaignForm, rooms: updatedRooms });
    }
  }

  function createCardText(campaignId: string) {
    if (campaignsData === null) return null;
    const campaignData = campaignsData.filter(
      (campaign) => campaign.id === campaignId,
    )[0];
    const numRooms = campaignData.rooms.length;
    return `${numRooms} ${
      campaignData.rooms.length === 1
        ? translateText("room", language, true)
        : translateText("rooms", language, true)
    }`;
  }

  async function confirmCreateCampaign(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    const {
      data_type: dataType,
      build_type: buildType,
      name,
      description,
      display_type: displayType,
      // document,
      background_image_selection: bgImageSelection,
      background_image: bgImage,
      header,
      primary_text: primaryText,
      secondary_text: secondaryText,
      title,
      body,
      choose_rooms_by: chooseRoomsBy,
      rooms,
      tags,
      template_type: templateType,
    } = createCampaignForm;

    // Validate text fields
    if (name[language].length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 3000),
      );
      return;
    }
    if (description[language].length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_description", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    if (displayType === "R") {
      if (header[language].length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_header", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      if (primaryText[language].length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_primary_text", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      if (secondaryText[language].length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_secondary_text", language),
            "danger",
            3000,
          ),
        );
        return;
      }
    } else {
      if (title[language].length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_title", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      if (body[language].length === 0) {
        dispatch(
          addAlert(generateAlertText("missing_body", language), "danger", 3000),
        );
        return;
      }
    }

    // Validate background image
    if (bgImageSelection === "catalog" && bgImage.length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_background_image", language),
          "danger",
          3000,
        ),
      );
      return;
    }

    // Build object for processed photos
    const processedPhotoKeys = Object.keys(images.campaigns.data);
    const processedPhotos = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.campaigns.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }
    if (
      bgImageSelection === "upload" &&
      (processedPhotos.length === 0 || processedPhotos.length > 1)
    ) {
      dispatch(
        addAlert(
          generateAlertText("missing_background_image", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    let chosenImage: any = { id: 0, source: "saved", data: bgImage };
    if (bgImageSelection === "upload") {
      chosenImage = processedPhotos[0];
    }

    // Build and validate rooms array
    let campaignRooms = rooms;
    if (chooseRoomsBy === "tag") {
      if (tags.length === 0) {
        dispatch(
          addAlert(generateAlertText("missing_tags", language), "danger", 3000),
        );
        return;
      }
      campaignRooms = [];
      for (let i = 0; i < roomsData.length; i++) {
        const roomTags = roomsData[i].tag;
        const commonTags = roomTags.filter((x: any) => tags.includes(x));
        if (commonTags.length > 0) {
          campaignRooms.push(roomsData[i].id);
        }
      }
    }
    if (campaignRooms.length === 0) {
      dispatch(
        addAlert(generateAlertText("missing_rooms", language), "danger", 3000),
      );
      return;
    }

    // Validate start and end dates (custom = start and end datetime, template = start and end time)
    if (buildType === "custom") {
      const { startDate } = createCustomCampaignStartDate;
      const { endDate } = createCustomCampaignEndDate;
      if (startDate === null) {
        dispatch(
          addAlert(
            generateAlertText("missing_start_date", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      if (endDate === null) {
        dispatch(
          addAlert(
            generateAlertText("missing_end_date", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      const { hour: startHour, minute: startMinute } =
        createCustomCampaignStartTime;
      const { hour: endHour, minute: endMinute } = createCustomCampaignEndTime;
      if (startHour === "" || startMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_start_time", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      if (endHour === "" || endMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_end_time", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      const startDatetime = buildDatetimeObjectFromStringAndNumbers(
        startDate,
        startHour,
        startMinute,
      );
      const endDatetime = buildDatetimeObjectFromStringAndNumbers(
        endDate,
        endHour,
        endMinute,
      );
      if (startDatetime >= endDatetime) {
        dispatch(
          addAlert(
            generateAlertText("start_after_end", language),
            "danger",
            3000,
          ),
        );
        return;
      }
      const now = new Date();
      if (startDatetime <= now) {
        dispatch(
          addAlert(
            generateAlertText("start_before_now", language),
            "danger",
            3000,
          ),
        );
        return;
      }
    } else {
      if (dataType === "static") {
        const { startDate } = createCustomCampaignStartDate;
        const { endDate } = createCustomCampaignEndDate;
        if (startDate === null) {
          dispatch(
            addAlert(
              generateAlertText("missing_start_date", language),
              "danger",
              3000,
            ),
          );
          return;
        }
        if (endDate === null) {
          dispatch(
            addAlert(
              generateAlertText("missing_end_date", language),
              "danger",
              3000,
            ),
          );
          return;
        }
        const { hour: startHour, minute: startMinute } =
          createCustomCampaignStartTime;
        const { hour: endHour, minute: endMinute } =
          createCustomCampaignEndTime;
        if (startHour === "" || startMinute === "") {
          dispatch(
            addAlert(
              generateAlertText("missing_start_time", language),
              "danger",
              3000,
            ),
          );
          return;
        }
        if (endHour === "" || endMinute === "") {
          dispatch(
            addAlert(
              generateAlertText("missing_end_time", language),
              "danger",
              3000,
            ),
          );
          return;
        }
        const startDatetime = buildDatetimeObjectFromStringAndNumbers(
          startDate,
          startHour,
          startMinute,
        );
        const endDatetime = buildDatetimeObjectFromStringAndNumbers(
          endDate,
          endHour,
          endMinute,
        );
        if (startDatetime >= endDatetime) {
          dispatch(
            addAlert(
              generateAlertText("start_after_end", language),
              "danger",
              3000,
            ),
          );
          return;
        }
        const now = new Date();
        if (startDatetime <= now) {
          dispatch(
            addAlert(
              generateAlertText("start_before_now", language),
              "danger",
              3000,
            ),
          );
          return;
        }
      } else {
        if (createDynamicCampaignScheduleType.value === "STD") {
          const { hour: startHour, minute: startMinute } =
            createDynamicCampaignScheduleStdStart;
          const { hour: endHour, minute: endMinute } =
            createDynamicCampaignScheduleStdEnd;
          if (startHour === "" || startMinute === "") {
            dispatch(
              addAlert(
                generateAlertText("missing_start_time", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          if (endHour === "" || endMinute === "") {
            dispatch(
              addAlert(
                generateAlertText("missing_end_time", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          const startObject = buildDateObjectFromHourAndMinute(
            startHour,
            startMinute,
          );
          const endObject = buildDateObjectFromHourAndMinute(
            endHour,
            endMinute,
          );
          if (startObject >= endObject) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        } else if (createDynamicCampaignScheduleType.value === "STW") {
          if (createDynamicCampaignScheduleStw.length === 0) {
            dispatch(
              addAlert(
                generateAlertText("missing_days_of_the_week", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          const { hour: startHour, minute: startMinute } =
            createDynamicCampaignScheduleStwStart;
          const { hour: endHour, minute: endMinute } =
            createDynamicCampaignScheduleStwEnd;
          if (startHour === "" || startMinute === "") {
            dispatch(
              addAlert(
                generateAlertText("missing_start_time", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          if (endHour === "" || endMinute === "") {
            dispatch(
              addAlert(
                generateAlertText("missing_end_time", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          const startObject = buildDateObjectFromHourAndMinute(
            startHour,
            startMinute,
          );
          const endObject = buildDateObjectFromHourAndMinute(
            endHour,
            endMinute,
          );
          if (startObject >= endObject) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        } else if (createDynamicCampaignScheduleType.value === "STM") {
          if (createDynamicCampaignScheduleStm.length === 0) {
            dispatch(
              addAlert(
                generateAlertText("missing_days_of_the_month", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          const { hour: startHour, minute: startMinute } =
            createDynamicCampaignScheduleStmStart;
          const { hour: endHour, minute: endMinute } =
            createDynamicCampaignScheduleStmEnd;
          if (startHour === "" || startMinute === "") {
            dispatch(
              addAlert(
                generateAlertText("missing_start_time", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          if (endHour === "" || endMinute === "") {
            dispatch(
              addAlert(
                generateAlertText("missing_end_time", language),
                "danger",
                3000,
              ),
            );
            return;
          }
          const startObject = buildDateObjectFromHourAndMinute(
            startHour,
            startMinute,
          );
          const endObject = buildDateObjectFromHourAndMinute(
            endHour,
            endMinute,
          );
          if (startObject >= endObject) {
            dispatch(
              addAlert(
                generateAlertText("start_after_end", language),
                "danger",
                3000,
              ),
            );
            return;
          }
        }
      }
    }

    // If campaign is a pinned campaign
    if (displayType === "P") {
      // Send pinned campaign action
      const obj = {
        name,
        description,
        property: user.property.id,
        backgroundImage: chosenImage,
        title,
        body,
        rooms: campaignRooms,
        schedule: "O",
        scheduleDetail: {
          O: {
            start_date: createCustomCampaignStartDate.startDate,
            start_time: {
              hour: parseInt(createCustomCampaignStartTime.hour, 10),
              minute: parseInt(createCustomCampaignStartTime.minute, 10),
            },
            end_date: createCustomCampaignEndDate.endDate,
            end_time: {
              hour: parseInt(createCustomCampaignEndTime.hour, 10),
              minute: parseInt(createCustomCampaignEndTime.minute, 10),
            },
          },
          R: { STD: null, STW: null, STM: null },
        },
        status: "A",
      };
      setShowCreateModal(false);
      const res: any = await dispatch(createPinnedCampaign(obj));
      if (res.payload.status === 200) {
        dispatch(
          addAlert(
            generateAlertText("create_campaign", language),
            "success",
            3000,
          ),
        );
        setCreateCampaignForm({
          data_type: "static",
          build_type: "custom",
          name: { EN: "", FR: "", IT: "", DE: "" },
          description: { EN: "", FR: "", IT: "", DE: "" },
          display_type: "R",
          document: "D",
          background_image_selection: "catalog",
          background_image:
            "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/purple.png",
          background_image_array: [],
          header: { EN: "", FR: "", IT: "", DE: "" },
          primary_text: { EN: "", FR: "", IT: "", DE: "" },
          secondary_text: { EN: "", FR: "", IT: "", DE: "" },
          title: { EN: "", FR: "", IT: "", DE: "" },
          body: { EN: "", FR: "", IT: "", DE: "" },
          choose_rooms_by: "name",
          rooms: [],
          tags: [],
          template_type: "",
        });
      } else {
        dispatch(
          addAlert(generateAlertText("create_error", language), "danger", 3000),
        );
        setShowCreateModal(true);
      }
      return;
    } else if (displayType === "R") {
      if (dataType === "static") {
        // Send static rotating campaign action
        const obj = {
          name,
          description,
          property: user.property.id,
          backgroundImage: chosenImage,
          headerText: header,
          primaryText,
          secondaryText,
          rooms: campaignRooms,
          schedule: "O",
          scheduleDetail: {
            O: {
              start_date: createCustomCampaignStartDate.startDate,
              start_time: {
                hour: parseInt(createCustomCampaignStartTime.hour, 10),
                minute: parseInt(createCustomCampaignStartTime.minute, 10),
              },
              end_date: createCustomCampaignEndDate.endDate,
              end_time: {
                hour: parseInt(createCustomCampaignEndTime.hour, 10),
                minute: parseInt(createCustomCampaignEndTime.minute, 10),
              },
            },
            R: { STD: null, STW: null, STM: null },
          },
          status: "A",
        };
        setShowCreateModal(false);
        const res: any = await dispatch(createRotatingCampaign(obj));
        if (res.payload.status === 200) {
          dispatch(
            addAlert(
              generateAlertText("create_campaign", language),
              "success",
              3000,
            ),
          );
          setCreateCampaignForm({
            data_type: "static",
            build_type: "custom",
            name: { EN: "", FR: "", IT: "", DE: "" },
            description: { EN: "", FR: "", IT: "", DE: "" },
            display_type: "R",
            document: "D",
            background_image_selection: "catalog",
            background_image:
              "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/purple.png",
            background_image_array: [],
            header: { EN: "", FR: "", IT: "", DE: "" },
            primary_text: { EN: "", FR: "", IT: "", DE: "" },
            secondary_text: { EN: "", FR: "", IT: "", DE: "" },
            title: { EN: "", FR: "", IT: "", DE: "" },
            body: { EN: "", FR: "", IT: "", DE: "" },
            choose_rooms_by: "name",
            rooms: [],
            tags: [],
            template_type: "",
          });
        } else {
          dispatch(
            addAlert(
              generateAlertText("create_error", language),
              "danger",
              3000,
            ),
          );
          setShowCreateModal(true);
        }
        return;
      } else if (dataType === "dynamic") {
        // Send dynamic rotating campaign action
        const obj = {
          name,
          description,
          property: user.property.id,
          templateType,
          displayType,
          dynamicData: dataType === "dynamic",
          backgroundImageUrlArray: [chosenImage],
          rooms: campaignRooms,
          schedule: "R",
          scheduleDetail: {
            O: null,
            R: {
              STD:
                createDynamicCampaignScheduleType.value === "STD"
                  ? {
                      start_date: formatDateAsString(new Date()),
                      start_time: {
                        hour: parseInt(
                          createDynamicCampaignScheduleStdStart.hour,
                          10,
                        ),
                        minute: parseInt(
                          createDynamicCampaignScheduleStdStart.minute,
                          10,
                        ),
                      },
                      end_time: {
                        hour: parseInt(
                          createDynamicCampaignScheduleStdEnd.hour,
                          10,
                        ),
                        minute: parseInt(
                          createDynamicCampaignScheduleStdEnd.minute,
                          10,
                        ),
                      },
                    }
                  : null,
              STW:
                createDynamicCampaignScheduleType.value === "STW"
                  ? {
                      day_of_week: createDynamicCampaignScheduleStw,
                      start_date: formatDateAsString(new Date()),
                      start_time: {
                        hour: parseInt(
                          createDynamicCampaignScheduleStwStart.hour,
                          10,
                        ),
                        minute: parseInt(
                          createDynamicCampaignScheduleStwStart.minute,
                          10,
                        ),
                      },
                      end_time: {
                        hour: parseInt(
                          createDynamicCampaignScheduleStwEnd.hour,
                          10,
                        ),
                        minute: parseInt(
                          createDynamicCampaignScheduleStwEnd.minute,
                          10,
                        ),
                      },
                    }
                  : null,
              STM:
                createDynamicCampaignScheduleType.value === "STW"
                  ? {
                      day_of_month: createDynamicCampaignScheduleStm,
                      start_date: formatDateAsString(new Date()),
                      start_time: {
                        hour: parseInt(
                          createDynamicCampaignScheduleStmStart.hour,
                          10,
                        ),
                        minute: parseInt(
                          createDynamicCampaignScheduleStmStart.minute,
                          10,
                        ),
                      },
                      end_time: {
                        hour: parseInt(
                          createDynamicCampaignScheduleStmEnd.hour,
                          10,
                        ),
                        minute: parseInt(
                          createDynamicCampaignScheduleStmEnd.minute,
                          10,
                        ),
                      },
                    }
                  : null,
            },
          },
          status: "A",
        };
        setShowCreateModal(false);
        const res: any = await dispatch(createTemplateCampaign(obj));
        if (res.payload.status === 200) {
          dispatch(
            addAlert(
              generateAlertText("create_campaign", language),
              "success",
              3000,
            ),
          );
          setCreateCampaignForm({
            data_type: "static",
            build_type: "custom",
            name: { EN: "", FR: "", IT: "", DE: "" },
            description: { EN: "", FR: "", IT: "", DE: "" },
            display_type: "R",
            document: "D",
            background_image_selection: "catalog",
            background_image:
              "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/purple.png",
            background_image_array: [],
            header: { EN: "", FR: "", IT: "", DE: "" },
            primary_text: { EN: "", FR: "", IT: "", DE: "" },
            secondary_text: { EN: "", FR: "", IT: "", DE: "" },
            title: { EN: "", FR: "", IT: "", DE: "" },
            body: { EN: "", FR: "", IT: "", DE: "" },
            choose_rooms_by: "name",
            rooms: [],
            tags: [],
            template_type: "",
          });
        } else {
          dispatch(
            addAlert(
              generateAlertText("create_error", language),
              "danger",
              3000,
            ),
          );
          setShowCreateModal(true);
        }
        return;
      }
    }
  }

  async function confirmDeleteCampaign(e: any) {
    e.preventDefault();
    if (selectedCampaign === null) return;
    const campaignId = selectedCampaign.id;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (campaignId === null || campaignId === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_campaign", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(
      deleteCampaigns({ campaignIds: [campaignId] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function selectOrUnselectAllTags(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableTags = roomTags.map((x: any) => x.id);
      setCreateCampaignForm({ ...createCampaignForm, tags: allAvailableTags });
    } else {
      setCreateCampaignForm({ ...createCampaignForm, tags: [] });
    }
  }

  function updateTagForCampaign(tag: string) {
    const existingTags = createCampaignForm.tags;
    if (existingTags.includes(tag)) {
      const updatedTags = existingTags.filter((x) => x !== tag);
      setCreateCampaignForm({ ...createCampaignForm, tags: updatedTags });
    } else {
      const updatedTags = [...existingTags, tag];
      setCreateCampaignForm({ ...createCampaignForm, tags: updatedTags });
    }
  }

  function updateTemplateType(e: any) {
    e.preventDefault();
    const templateType = e.target.value;
    const templateData = filteredCampaignTemplates.filter(
      (x) => x.template_type === templateType,
    )[0];
    setCreateCampaignForm(templateData);
  }

  function selectOrUnselectAllDow(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableDow = daysOfTheWeek.map((x: any) => x[0]);
      setCreateDynamicCampaignScheduleStw(allAvailableDow);
    } else {
      setCreateDynamicCampaignScheduleStw([]);
    }
  }

  function selectOrUnselectAllDom(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableDom = daysOfTheMonth.map((x: any) => x[0]);
      setCreateDynamicCampaignScheduleStm(allAvailableDom);
    } else {
      setCreateDynamicCampaignScheduleStm([]);
    }
  }

  function updateDowForDynamicCampaign(dow: any) {
    if (createDynamicCampaignScheduleStw.includes(dow)) {
      const updatedDow = createDynamicCampaignScheduleStw.filter(
        (x) => x !== dow,
      );
      setCreateDynamicCampaignScheduleStw(updatedDow);
    } else {
      const updatedDow = [...createDynamicCampaignScheduleStw, dow];
      setCreateDynamicCampaignScheduleStw(updatedDow);
    }
  }

  function updateDomForDynamicCampaign(dom: any) {
    if (createDynamicCampaignScheduleStm.includes(dom)) {
      const updatedDom = createDynamicCampaignScheduleStm.filter(
        (x) => x !== dom,
      );
      setCreateDynamicCampaignScheduleStm(updatedDom);
    } else {
      const updatedDom = [...createDynamicCampaignScheduleStm, dom];
      setCreateDynamicCampaignScheduleStm(updatedDom);
    }
  }

  function generatePreviewImage() {
    const backup = `url(${createCampaignForm.background_image})`;

    // Check if image has been uploaded
    if (createCampaignForm.background_image_selection === "upload") {
      const candidates = campaignImages.data;
      const keys = Object.keys(candidates);
      if (keys.length === 0) return backup;
      const data = candidates[Object.keys(candidates)[0]].data;
      return `url('data:image/png;base64, ${data}')`;
    }

    // Otherwise, choose image that user has selected from range
    return backup;
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const campaignsData = campaigns.data === null ? [] : campaigns.data;
  // const dataTypeChoices = [
  //   ["static", translateText("static", language)],
  //   ["dynamic", translateText("dynamic", language)],
  // ];
  const buildTypes = [
    ["template", translateText("template", language)],
    ["custom", translateText("custom", language)],
  ];
  const displayTypeChoices = [
    ["R", translateText("rotating", language)],
    ["P", translateText("pinned", language)],
  ];
  const backgroundImageSelectionChoices = [
    ["catalog", translateText("choose_from_catalog", language)],
    ["upload", translateText("upload", language)],
  ];
  const backgroundImageCatalogChoices = [
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/purple.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/blue.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/green.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/yellow.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/orange.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/clouds.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/forest.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/fruit.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/night-sky.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/ocean.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/sunset.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/alarm.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/maintenance.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/meal.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/music.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/news.png",
    "https://alexa-apl-graphics.s3.eu-west-2.amazonaws.com/audico/background/radio.png",
  ];
  const chooseRoomsBy = [
    ["name", translateText("name", language)],
    ["tag", translateText("tag_single", language)],
  ];

  // Create tags object for filters
  let roomTags: any = [];
  for (let i = 0; i < roomsData.length; i++) {
    const candidateTags = roomsData[i].tag;
    for (let j = 0; j < candidateTags.length; j++) {
      if (!roomTags.includes(candidateTags[j])) {
        roomTags.push(candidateTags[j]);
      }
    }
  }
  roomTags.sort();
  roomTags = roomTags.map((x: any) => ({ id: x, label: x }));
  const formattedCampaignTemplates = campaignTemplates.map((x: any) => ({
    ...x,
    name: x.name,
    description: x.description,
    header:
      x.header.length === 0 ? { EN: "", FR: "", IT: "", DE: "" } : x.header,
    primary_text:
      x.primary_text.length === 0
        ? { EN: "", FR: "", IT: "", DE: "" }
        : x.primary_text,
    secondary_text:
      x.secondary_text.length === 0
        ? { EN: "", FR: "", IT: "", DE: "" }
        : x.secondary_text,
    title: x.title.length === 0 ? { EN: "", FR: "", IT: "", DE: "" } : x.title,
    body: x.body.length === 0 ? { EN: "", FR: "", IT: "", DE: "" } : x.body,
  }));
  const filteredCampaignTemplates = formattedCampaignTemplates.filter(
    (x) => x.data_type === createCampaignForm.data_type,
  );
  const dynamicScheduleChoices =
    options.recurring_choices === undefined
      ? []
      : Object.keys(options.recurring_choices).map((x) => [
          x,
          options.recurring_choices[x][language],
        ]);
  const daysOfTheWeek = daysOfWeek(language);
  const daysOfTheMonth = daysOfMonth();
  const showPreview =
    createCampaignForm.header[language].length > 0 ||
    createCampaignForm.primary_text[language].length > 0 ||
    createCampaignForm.secondary_text[language].length > 0 ||
    createCampaignForm.title[language].length > 0 ||
    createCampaignForm.body[language].length > 0;

  const campaignTypeOptions = [
    { value: "A", label: translateText("all", language) },
    { value: "R", label: translateText("rotating", language) },
    { value: "P", label: translateText("pinned", language) },
    { value: "T", label: translateText("template_single", language) },
  ];

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("campaigns_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateCampaignModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_campaign", language)}
              </Button>
            }
          />
        }
      />

      {/* Campaign preview */}
      {showPreview && showCreateModal && (
        <div className="z-50 fixed top-0 right-0">
          <div
            className="sm:h-52 sm:w-80 h-32 w-48 bg-cover rounded-bl-md border-l-2 border-b-2 border-white"
            style={{
              backgroundImage: generatePreviewImage(),
              filter: "brightness(75%)",
            }}
          />
          <div className="grid grid-cols-1 absolute left-4 bottom-4">
            {createCampaignForm.display_type === "R" && (
              <>
                <p className="text-white text-wrap sm:text-sm text-xs">
                  {createCampaignForm.header[language]}
                </p>
                <p className="text-white text-wrap sm:text-md text-sm font-semibold">
                  {createCampaignForm.primary_text[language]}
                </p>
                <p className="text-white/80 text-wrap sm:text-sm text-xs">
                  {createCampaignForm.secondary_text[language]}
                </p>
              </>
            )}
            {createCampaignForm.display_type === "P" && (
              <>
                <p className="text-white text-wrap sm:text-sm text-xs">
                  {createCampaignForm.title[language]}
                </p>
                <p className="text-white text-wrap sm:text-md text-sm font-semibold">
                  {createCampaignForm.body[language]}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Create campaign modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_campaign", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateCampaign}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_template", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Build type */}
              <HorizontalRadio
                label={translateText("build_type", language)}
                small={translateText("build_type_subtext", language)}
              >
                <>
                  {buildTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={
                          createCampaignForm.build_type === choice[0]
                        }
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateCampaignForm({
                            ...createCampaignForm,
                            build_type: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {/* Static or dynamic data */}
              {/* {createCampaignForm.build_type === "template" && (
                <HorizontalRadio
                  label={translateText("content_campaign_type", language)}
                  small={translateText(
                    "content_campaign_type_subtext",
                    language,
                  )}
                >
                  <>
                    {dataTypeChoices.map((choice) => (
                      <div key={choice[0]} className="flex items-center">
                        <input
                          id={choice[0]}
                          name="data_type"
                          type="radio"
                          defaultChecked={
                            createCampaignForm.data_type === choice[0]
                          }
                          className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                          value={choice[0]}
                          onChange={(e) =>
                            setCreateCampaignForm({
                              ...createCampaignForm,
                              data_type: e.target.value,
                            })
                          }
                        />
                        <label
                          htmlFor={choice[0]}
                          className="ml-3 block text-sm font-medium leading-6 text-black"
                        >
                          {choice[1]}
                        </label>
                      </div>
                    ))}
                  </>
                </HorizontalRadio>
              )} */}

              {/* Choose template type if template selected */}
              {createCampaignForm.build_type === "template" && (
                <div className="my-auto">
                  <p className="text-xs text-left text-secondary italic mb-3">
                    {translateText("template_type_subtext", language)}
                  </p>
                  <div className="col-span-full">
                    <select
                      id="template_type"
                      className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      required={true}
                      onChange={(e) => updateTemplateType(e)}
                      defaultValue=""
                    >
                      <option value="">
                        {translateText("select_template_type", language)}
                      </option>
                      {filteredCampaignTemplates.map((template) => (
                        <option
                          key={template.template_type}
                          value={template.template_type}
                        >
                          {template.name[language]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createCampaignForm}
                stateName="name"
                onChange={setCreateCampaignForm}
                placeholder={translateText(
                  "name_campaign_placeholder",
                  language,
                )}
                defaultLanguage={language}
                languageChoices={
                  campaignsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(campaignsData[0].name)
                }
              />

              {/* Description */}
              <TextInputMultiLanguage
                id="description"
                type="text"
                label={translateText("description", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createCampaignForm}
                stateName="description"
                onChange={setCreateCampaignForm}
                placeholder={translateText(
                  "description_campaign_placeholder",
                  language,
                )}
                defaultLanguage={language}
                languageChoices={
                  campaignsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(campaignsData[0].name)
                }
              />

              {/* Display type */}
              {createCampaignForm.data_type === "static" && (
                <HorizontalRadio
                  label={translateText(
                    "content_campaign_display_type",
                    language,
                  )}
                  small={translateText(
                    "content_campaign_display_type_subtext",
                    language,
                  )}
                >
                  <>
                    {displayTypeChoices.map((choice) => (
                      <div key={choice[0]} className="flex items-center">
                        <input
                          id={choice[0]}
                          name="display_type"
                          type="radio"
                          defaultChecked={
                            createCampaignForm.display_type === choice[0]
                          }
                          className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                          value={choice[0]}
                          onChange={(e) =>
                            setCreateCampaignForm({
                              ...createCampaignForm,
                              display_type: e.target.value,
                            })
                          }
                        />
                        <label
                          htmlFor={choice[0]}
                          className="ml-3 block text-sm font-medium leading-6 text-black"
                        >
                          {choice[1]}
                        </label>
                      </div>
                    ))}
                  </>
                </HorizontalRadio>
              )}

              {/* Background image selection */}
              <HorizontalRadio
                label={translateText("background_image", language)}
                small={translateText(
                  "background_image_campaign_subtext",
                  language,
                )}
              >
                <>
                  {backgroundImageSelectionChoices.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="background_image_selection"
                        type="radio"
                        defaultChecked={
                          createCampaignForm.background_image_selection ===
                          choice[0]
                        }
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateCampaignForm({
                            ...createCampaignForm,
                            background_image_selection: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>
              {createCampaignForm.background_image_selection === "catalog" ? (
                <div className="grid grid-cols-4 gap-2">
                  {backgroundImageCatalogChoices.map((image, index) => (
                    <img
                      key={index}
                      className={`rounded-md ${
                        createCampaignForm.background_image === image &&
                        "ring-2 ring-success animate-bounce"
                      } hover:ring-2 hover:ring-primary`}
                      src={image}
                      alt={`Catalog background ${index + 1}`}
                      onClick={() =>
                        setCreateCampaignForm({
                          ...createCampaignForm,
                          background_image: image,
                        })
                      }
                      aria-hidden="true"
                    />
                  ))}
                </div>
              ) : (
                <UploadFileDropzone
                  id="campaign_background_image"
                  className=""
                  limit={1}
                  page="campaigns"
                  savedImages={[]}
                  blockId={null}
                />
              )}

              {createCampaignForm.data_type === "static" &&
                createCampaignForm.display_type === "R" && (
                  <>
                    {/* Header */}
                    <TextInputMultiLanguage
                      id="header"
                      type="text"
                      label={translateText("header", language)}
                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      required={true}
                      showLabel={true}
                      state={createCampaignForm}
                      stateName="header"
                      onChange={setCreateCampaignForm}
                      placeholder={translateText(
                        "header_placeholder",
                        language,
                      )}
                      defaultLanguage={language}
                      languageChoices={
                        campaignsData.length === 0
                          ? ["EN", "FR", "IT", "DE"]
                          : Object.keys(campaignsData[0].name)
                      }
                    />

                    {/* Primary text */}
                    <TextInputMultiLanguage
                      id="primary_text"
                      type="text"
                      label={translateText("primary_text", language)}
                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      required={true}
                      showLabel={true}
                      state={createCampaignForm}
                      stateName="primary_text"
                      onChange={setCreateCampaignForm}
                      placeholder={translateText(
                        "primary_text_placeholder",
                        language,
                      )}
                      defaultLanguage={language}
                      languageChoices={
                        campaignsData.length === 0
                          ? ["EN", "FR", "IT", "DE"]
                          : Object.keys(campaignsData[0].name)
                      }
                    />

                    {/* Secondary text */}
                    <TextInputMultiLanguage
                      id="secondary_text"
                      type="text"
                      label={translateText("secondary_text", language)}
                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      required={true}
                      showLabel={true}
                      state={createCampaignForm}
                      stateName="secondary_text"
                      onChange={setCreateCampaignForm}
                      placeholder={translateText(
                        "secondary_text_placeholder",
                        language,
                      )}
                      defaultLanguage={language}
                      languageChoices={
                        campaignsData.length === 0
                          ? ["EN", "FR", "IT", "DE"]
                          : Object.keys(campaignsData[0].name)
                      }
                    />
                  </>
                )}
              {createCampaignForm.data_type === "static" &&
                createCampaignForm.display_type === "P" && (
                  <>
                    {/* Title */}
                    <TextInputMultiLanguage
                      id="title"
                      type="text"
                      label={translateText("title", language)}
                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      required={true}
                      showLabel={true}
                      state={createCampaignForm}
                      stateName="title"
                      onChange={setCreateCampaignForm}
                      placeholder={translateText("title_placeholder", language)}
                      defaultLanguage={language}
                      languageChoices={
                        campaignsData.length === 0
                          ? ["EN", "FR", "IT", "DE"]
                          : Object.keys(campaignsData[0].name)
                      }
                    />

                    {/* Body */}
                    <TextInputMultiLanguage
                      id="body"
                      type="text"
                      label={translateText("body", language)}
                      className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      required={true}
                      showLabel={true}
                      state={createCampaignForm}
                      stateName="body"
                      onChange={setCreateCampaignForm}
                      placeholder={translateText("body_placeholder", language)}
                      defaultLanguage={language}
                      languageChoices={
                        campaignsData.length === 0
                          ? ["EN", "FR", "IT", "DE"]
                          : Object.keys(campaignsData[0].name)
                      }
                    />
                  </>
                )}

              {/* Choose rooms by */}
              <Dropdown
                id="choose_rooms_by"
                className="col-span-full"
                label={translateText("choose_rooms_by", language)}
                small={translateText("choose_rooms_by_subtext", language)}
                showLabel={true}
                required={true}
                state={createCampaignForm}
                stateName="choose_rooms_by"
                onChange={setCreateCampaignForm}
                defaultValue="name"
              >
                {chooseRoomsBy.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Rooms */}
              {createCampaignForm.choose_rooms_by === "name" ? (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForCampaign}
                        checked={createCampaignForm.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("tag", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText(
                        "select_tags_for_rooms_campaign",
                        language,
                      )}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllTags}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomTags.map((tag: any) => (
                      <Checkbox
                        key={tag.id}
                        id={tag.id}
                        onChange={updateTagForCampaign}
                        checked={createCampaignForm.tags.includes(tag.id)}
                        label={tag.label}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Schedule start and end datetime */}
              {createCampaignForm.data_type === "static" && (
                <>
                  <DatetimeSelector
                    label={translateText("start_datetime", language)}
                    showLabel={true}
                    dateState={createCustomCampaignStartDate}
                    dateHandler={setCreateCustomCampaignStartDate}
                    timeState={createCustomCampaignStartTime}
                    timeHandler={setCreateCustomCampaignStartTime}
                    language={language}
                  />
                  <DatetimeSelector
                    label={translateText("end_datetime", language)}
                    showLabel={true}
                    dateState={createCustomCampaignEndDate}
                    dateHandler={setCreateCustomCampaignEndDate}
                    timeState={createCustomCampaignEndTime}
                    timeHandler={setCreateCustomCampaignEndTime}
                    language={language}
                  />
                </>
              )}

              {/* Schedule details for dynamic data */}
              {createCampaignForm.build_type === "template" &&
                createCampaignForm.data_type === "dynamic" && (
                  <>
                    <Dropdown
                      id="dynamic_campaign_schedule_type"
                      className="col-span-full"
                      label={translateText("schedule", language)}
                      small={translateText(
                        "schedule_dynamic_campaign_subtext",
                        language,
                      )}
                      showLabel={true}
                      required={true}
                      state={createDynamicCampaignScheduleType}
                      stateName="value"
                      onChange={setCreateDynamicCampaignScheduleType}
                      defaultValue="STD"
                    >
                      {dynamicScheduleChoices.map((opt) => (
                        <option key={opt[0]} value={opt[0]}>
                          {opt[1]}
                        </option>
                      ))}
                    </Dropdown>
                    {createDynamicCampaignScheduleType.value === "STD" && (
                      <>
                        <TimeSelector
                          label={translateText("from", language)}
                          showLabel={true}
                          timeState={createDynamicCampaignScheduleStdStart}
                          timeHandler={setCreateDynamicCampaignScheduleStdStart}
                          language={language}
                        />
                        <TimeSelector
                          label={translateText("until", language)}
                          showLabel={true}
                          timeState={createDynamicCampaignScheduleStdEnd}
                          timeHandler={setCreateDynamicCampaignScheduleStdEnd}
                          language={language}
                        />
                      </>
                    )}
                    {createDynamicCampaignScheduleType.value === "STW" && (
                      <>
                        <div>
                          <label className="mb-1 block text-left text-sm font-medium text-black">
                            {translateText("days_of_week", language)}
                          </label>
                          <p className="text-left text-xs italic text-secondary">
                            {translateText(
                              "days_of_week_campaign_subtext",
                              language,
                            )}
                          </p>
                        </div>

                        {/* Select/unselect all buttons */}
                        <SelectOrUnselectAll
                          handler={selectOrUnselectAllDow}
                          language={language}
                        />
                        <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                          {daysOfTheWeek.map((dow: any) => (
                            <Checkbox
                              key={dow[0]}
                              id={dow[0]}
                              onChange={updateDowForDynamicCampaign}
                              checked={createDynamicCampaignScheduleStw.includes(
                                dow[0],
                              )}
                              label={dow[1]}
                            />
                          ))}
                        </div>
                        <TimeSelector
                          label={translateText("from", language)}
                          showLabel={true}
                          timeState={createDynamicCampaignScheduleStwStart}
                          timeHandler={setCreateDynamicCampaignScheduleStwStart}
                          language={language}
                        />
                        <TimeSelector
                          label={translateText("until", language)}
                          showLabel={true}
                          timeState={createDynamicCampaignScheduleStwEnd}
                          timeHandler={setCreateDynamicCampaignScheduleStwEnd}
                          language={language}
                        />
                      </>
                    )}
                    {createDynamicCampaignScheduleType.value === "STM" && (
                      <>
                        <div>
                          <label className="mb-1 block text-left text-sm font-medium text-black">
                            {translateText("days_of_month", language)}
                          </label>
                          <p className="text-left text-xs italic text-secondary">
                            {translateText(
                              "days_of_month_campaign_subtext",
                              language,
                            )}
                          </p>
                        </div>

                        {/* Select/unselect all buttons */}
                        <SelectOrUnselectAll
                          handler={selectOrUnselectAllDom}
                          language={language}
                        />
                        <div className="grid md:grid-cols-6 grid-cols-3 gap-2">
                          {daysOfTheMonth.map((dom: any) => (
                            <Checkbox
                              key={dom[0]}
                              id={dom[0]}
                              onChange={updateDomForDynamicCampaign}
                              checked={createDynamicCampaignScheduleStm.includes(
                                dom[0],
                              )}
                              label={dom[1]}
                            />
                          ))}
                        </div>
                        <TimeSelector
                          label={translateText("from", language)}
                          showLabel={true}
                          timeState={createDynamicCampaignScheduleStmStart}
                          timeHandler={setCreateDynamicCampaignScheduleStmStart}
                          language={language}
                        />
                        <TimeSelector
                          label={translateText("until", language)}
                          showLabel={true}
                          timeState={createDynamicCampaignScheduleStmEnd}
                          timeHandler={setCreateDynamicCampaignScheduleStmEnd}
                          language={language}
                        />
                      </>
                    )}
                  </>
                )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit campaign modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_campaign", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmDeleteCampaign}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {`Edit campaign "${selectedCampaign?.name[language]}"`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Created by */}
              <DisabledTextInput
                id="created_by"
                label={translateText("created_by", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedCampaign === null ? "" : selectedCampaign.created_by
                }
              />

              {/* Name */}
              <DisabledTextInput
                id="name"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedCampaign === null
                    ? ""
                    : selectedCampaign.name[language]
                }
              />

              {/* Description */}
              <DisabledTextInput
                id="description"
                label={translateText("description", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedCampaign === null
                    ? ""
                    : selectedCampaign.description[language]
                }
              />

              {/* Type */}
              <DisabledTextInput
                id="type"
                label={translateText("type", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedCampaign === null ? "" : selectedCampaign.type[1]
                }
              />

              {/* Background image */}
              {selectedCampaign !== null &&
                selectedCampaign.background_image !== null && (
                  <div>
                    <label
                      className={`mb-3 block text-left text-sm font-medium text-black`}
                    >
                      {translateText("background_image", language)}
                    </label>
                    <img
                      className={`rounded-md`}
                      src={selectedCampaign.background_image}
                      alt="Selected campaign background image"
                      aria-hidden="true"
                    />
                  </div>
                )}
              {/* {selectedCampaign !== null &&
                selectedCampaign.background_images.length > 0 && (
                  <div>
                    <label
                      className={`mb-3 block text-left text-sm font-medium text-black`}
                    >
                      {translateText("background_image", language)}
                    </label>
                    <img
                      className={`rounded-md`}
                      src={selectedCampaign.background_images[0]}
                      alt="Selected campaign background image"
                      aria-hidden="true"
                    />
                  </div>
                )} */}

              {/* Display data */}
              {selectedCampaign !== null &&
                selectedCampaign.type[0] === "P" && (
                  <>
                    {/* Title */}
                    <DisabledTextInput
                      id="title"
                      label={translateText("title", language)}
                      className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                      showLabel={true}
                      value={
                        selectedCampaign === null
                          ? ""
                          : selectedCampaign.display_data.title[language]
                      }
                    />
                    {/* Body */}
                    <DisabledTextInput
                      id="body"
                      label={translateText("body", language)}
                      className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                      showLabel={true}
                      value={
                        selectedCampaign === null
                          ? ""
                          : selectedCampaign.display_data.body[language]
                      }
                    />
                  </>
                )}
              {selectedCampaign !== null &&
                selectedCampaign.type[0] === "P" && (
                  <>
                    {/* Title */}
                    <DisabledTextInput
                      id="title"
                      label={translateText("title", language)}
                      className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                      showLabel={true}
                      value={
                        selectedCampaign === null
                          ? ""
                          : selectedCampaign.display_data.title[language]
                      }
                    />
                    {/* Body */}
                    <DisabledTextInput
                      id="body"
                      label={translateText("body", language)}
                      className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                      showLabel={true}
                      value={
                        selectedCampaign === null
                          ? ""
                          : selectedCampaign.display_data.body[language]
                      }
                    />
                  </>
                )}

              {/* Rooms */}
              <DisabledTextInput
                id="rooms"
                label={translateText("rooms", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedCampaign === null
                    ? ""
                    : selectedCampaign.rooms.join(", ")
                }
              />

              {/* Schedule detail */}
              <DisabledTextInput
                id="schedule_detail"
                label={translateText("schedule", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedCampaign === null
                    ? ""
                    : selectedCampaign.schedule_detail
                }
              />

              {/* Danger zone */}
              {selectedCampaign !== null && (
                <DangerZone
                  objectId={selectedCampaign.id}
                  handler={confirmDeleteCampaign}
                  language={language}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {campaigns.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "type",
                  name: "Type",
                  options: campaignTypeOptions,
                },
              ]}
              queryParams={[
                ["type", searchType],
                ["dateRange", dateRangeTerm],
              ]}
              handlers={{
                dateRange: {
                  state: dateRange,
                  stateName: "N/A",
                  onChange: setDateRange,
                },
                type: {
                  state: type,
                  stateName: "value",
                  onChange: setType,
                },
              }}
              loading={campaigns.loading}
              baseUrl="/content/campaigns"
              showDateRange={true}
              showFilters={true}
              showSearchBar={false}
            />
            {campaignsData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {campaignsData.map((campaign) => (
                    <a
                      href="#!"
                      key={campaign.id}
                      onClick={(e) => selectCampaignToEdit(e, [campaign.id])}
                    >
                      <Card
                        key={campaign.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {campaign.name[language]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <PhotoIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {campaign.type[1]}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <UsersIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {createCardText(campaign.id)}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {campaign.next_occurrence.string}
                          </div>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_campaigns", language)}
                subtext={translateText("no_campaigns_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
